// React
import React, { useEffect, useState } from "react";

// Third Party
import { SnackbarProvider } from "notistack";
import { SnackbarsCustomStyle, AUTO_HIDE_DURATION, MAX_SNACK } from "@Layouts/components/Snackbars";

// State Management and Utilities
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Context and Hooks
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import { useCacheAndLocations } from "@DataPages/hooks/users/usersHooks";

// API
import { getUsersByStatus, importPaychexEmployees, onUserIngestion, retrieveUnenrolledEmployees } from "@DataPages/api/users";
import { getUsersAndEmployeesPositions } from "@/DataPages/api/users/queries";
import { retrieveLocation } from "@/Common/api/location";

// Helpers
import { findInParentInTree } from "@Common/helpers/Arrays/tree";
import { errorLogger } from "@/Common/helpers/logger";

// Constants
import actionsList from "@Constants/actionsList";

// Components
import AppSideBar from "@/Layouts/components/SideBars/AppSideBar";
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import UsersBody from "./UsersBody";

// MUI
import { Skeleton } from "@mui/material";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import DoneIcon from "@mui/icons-material/Done";

// Types
import { ILocation } from "@/Common/interfaces/location";
import { IParent, ISelectedItem } from "@DataPages/types/users/users";
import { IEmployee } from "@/Common/interfaces/employee";
import { IUser } from "@/Common/interfaces/user";
import { ISideTreeChild } from "@/Layouts/components/SideBars/SideTreeView/interfaces";
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";

// Icons
import OptionsMenuIcon from "@/assets/icons/svg/OptionsMenuIcon";

// Constants
const OPTIONS_ICON_CLASSES = "side-selector-more-btn";

/************************************** HELPERS **************************************/
const LoadingInnerTree = () => {
	const width = 130;
	const height = 15;
	return <Skeleton className="rounded-md ml-0" animation="wave" variant="rectangular" width={width} height={height} />;
};

const loadingList = (locationID: string) => {
	return [
		{
			_id: `${locationID}-active`,
			title: "Active",
			disabled: true,
			customComponent: {
				component: <LoadingInnerTree />,
			},
		},
		{
			_id: `${locationID}-inactive`,
			title: "Inactive",
			disabled: true,
			customComponent: {
				component: <LoadingInnerTree />,
			},
		},
		{
			_id: `${locationID}-unenrolled`,
			title: "Unenrolled",
			disabled: true,
			customComponent: {
				component: <LoadingInnerTree />,
			},
		},
	];
};

const initializeSideTreeData = (locations: ILocation[] | null): ISideTreeChild[] => {
	return (
		locations?.map((loc) => ({
			_id: loc.locationId,
			title: loc.name,
			bold: true,
			list: loadingList(loc.locationId),
		})) ?? []
	);
};
const getCommunication = (employee: Partial<IEmployee> | Partial<IUser>, type: string, usage: string): string | null => {
	const comm = employee.communications?.find(
		(communication) =>
			communication.communicationType?.toUpperCase() === type.toUpperCase() &&
			communication.usage.toUpperCase() === usage.toUpperCase()
	);
	return comm?.value ?? null;
};

interface ICreateUsersResponse {
	locationId?: string;
	activeUsersPositions: string[];
	inactiveUsersPositions: string[];
	unenrolledEmployeesPositions: string[];
}
const shouldResetSelected = (positions: ICreateUsersResponse, selected: ISelectedItem | null) => {
	const statusToPositionsMap = {
		Unenrolled: positions.unenrolledEmployeesPositions,
		Active: positions.activeUsersPositions,
		Inactive: positions.inactiveUsersPositions,
	};

	const positionStatus: keyof typeof statusToPositionsMap = selected?.parent?.status?.title;
	const title = selected?.title;
	if (!positionStatus || !title) {
		return false;
	}

	return !statusToPositionsMap[positionStatus]?.includes(title);
};

async function getDataTable(selected: ISelectedItem | null) {
	if (selected?.parent?.status?.title === "Unenrolled") {
		const usersData = await retrieveUnenrolledEmployees(selected?.parent?.branch?._id, selected?.title ?? "");
		return usersData?.map((employee: Partial<IEmployee>) => {
			return {
				_id: employee._id ?? "",
				fullName: employee.fullName ?? "",
				employeeId: employee.employeeId ?? "",
				locationId: employee.locationId ?? "",
				emailBusiness: getCommunication(employee, "email", "business"),
				emailPersonal: getCommunication(employee, "email", "personal"),
				hireDate: employee.hireDate,
				position: employee.position,
				accountStatus: employee.cognitoStatus ?? "NOT FOUND",
			};
		});
	} else {
		const filterStatus = selected?.parent?.status?.title.toUpperCase();
		const usersFilter = {
			locationId: selected?.parent?.branch?._id,
			status: filterStatus === "INACTIVE" ? "INACTIVE_TEMPORARY" : filterStatus,
			position: [selected?.title ?? ""],
			cognitoStatus: true,
		};
		const usersData = await getUsersByStatus(usersFilter);
		return usersData?.map((user: IUser) => {
			return {
				_id: user._id ?? "",
				fullName: user.fullName ?? "",
				employeeId: user.employeeId ?? "",
				locationId: user.locationId ?? "",
				emailBusiness: getCommunication(user, "email", "business"),
				emailPersonal: getCommunication(user, "email", "personal"),
				longevityDate: user.longevityDate,
				hireDate: user.hireDate,
				lastLoginDateTime: user.lastLoginDateTime,
				position: user.position,
				accountStatus: user.cognitoStatus ?? "NOT FOUND",
			};
		});
	}
}

const UsersLayer: React.FC = () => {
	const { clearCache } = useCacheCleaner();
	const { location, locations, loadingLocations } = useCacheAndLocations(clearCache);
	const locationId = location?.locationId ?? "";
	const queryClient = useQueryClient();
	const { useAuth } = useAuthorization();
	const canPullUserDataFromPayroll = useAuth(actionsList.SYNC_USERS);

	const [treeData, setTreeData] = useState<ISideTreeChild[]>([]);
	const [selected, setSelected] = useState<ISelectedItem | null>(null);
	const [isLoadingMutations, setIsLoadingMutations] = useState(false);

	const { getSubscription, addSubscription } = useSubscriptions();

	const usersSubscription = getSubscription(["users", selected?.parent?.branch?._id ?? locationId]);
	usersSubscription?.subscriptions?.onUserIngestion?.setCallback(
		async (data: GraphqlQueryResponse<"onUserIngestion", ICreateUsersResponse>["data"]) => {
			const queryKey = ["allEmployees", data.data?.onUserIngestion.locationId];
			const positions: ICreateUsersResponse = {
				activeUsersPositions: data.data?.onUserIngestion.activeUsersPositions as string[],
				inactiveUsersPositions: data.data?.onUserIngestion.inactiveUsersPositions as string[],
				unenrolledEmployeesPositions: data.data?.onUserIngestion.unenrolledEmployeesPositions as string[],
			};
			if (selected?.parent?.branch?._id === data.data?.onUserIngestion.locationId) {
				if (shouldResetSelected(positions, selected)) {
					setSelected(null);
				} else {
					const dataTable = await getDataTable(selected); // Refresh the table data
					queryClient.setQueryData(
						["positionTable", selected?.parent?.branch?._id, selected?.parent?.status?.title, selected?.title],
						dataTable
					);
				}
			}

			queryClient.setQueryData(queryKey, positions);
			buildTree(positions, data.data?.onUserIngestion.locationId);
		}
	);
	useEffect(() => {
		setTreeData(initializeSideTreeData(locations));
	}, [locations]);

	const buildTree = (
		positionsData: {
			activeUsersPositions: string[];
			inactiveUsersPositions: string[];
			unenrolledEmployeesPositions: string[];
		} = {
			activeUsersPositions: [],
			inactiveUsersPositions: [],
			unenrolledEmployeesPositions: [],
		},
		selectedLocationId = ""
	) => {
		const sideTreeData = new Map<
			string,
			{ positionsActive: Set<string>; positionsInactive: Set<string>; positionsUnenrolled: Set<string> }
		>();

		locations?.forEach((loc) => {
			if (loc.locationId === selectedLocationId) {
				sideTreeData.set(loc.locationId, {
					positionsActive: new Set(positionsData.activeUsersPositions),
					positionsInactive: new Set(positionsData.inactiveUsersPositions),
					positionsUnenrolled: new Set(positionsData.unenrolledEmployeesPositions),
				});
			}
		});

		const toSorted = (list: string[] = []) => [...list].sort((a: string, b: string) => a.localeCompare(b));
		const toSortedList = (list: string[] = []) => toSorted(list);

		const sideTree: ISideTreeChild[] =
			locations
				?.map((loc) => {
					if (loc.locationId === selectedLocationId) {
						const { positionsActive, positionsInactive, positionsUnenrolled } = sideTreeData.get(loc.locationId) ?? {};
						const listActive = Array.from(positionsActive ?? []);
						const listInactive = Array.from(positionsInactive ?? []);
						const listUnenrolled = Array.from(positionsUnenrolled ?? []);

						return {
							_id: loc.locationId,
							title: loc.name,
							bold: true,
							list: [
								{
									_id: `${loc.locationId}-active`,
									title: "Active",
									disabled: !listActive?.length,
									list: toSortedList(listActive).map((entry, i) => ({
										_id: `${loc.locationId}-active-${i}`,
										title: entry,
									})),
								},
								{
									_id: `${loc.locationId}-inactive`,
									title: "Inactive",
									disabled: !listInactive?.length,
									list: toSortedList(listInactive).map((entry, i) => ({
										_id: `${loc.locationId}-inactive-${i}`,
										title: entry,
									})),
								},
								{
									_id: `${loc.locationId}-unenrolled`,
									title: "Unenrolled",
									disabled: !listUnenrolled?.length,
									list: toSortedList(listUnenrolled).map((entry, i) => ({
										_id: `${loc.locationId}-unenrolled-${i}`,
										title: entry,
									})),
								},
							],
						};
					} else {
						return treeData?.find((branch) => branch["_id" as keyof object] === loc.locationId);
					}
				})
				?.filter((item): item is ISideTreeChild => item !== undefined) || [];
		setTreeData(sideTree);
	};

	useQuery({
		queryKey: ["usersPageLastUpdate", location?.locationId],
		queryFn: async () => {
			try {
				const results = await retrieveLocation(locationId);
				return results?.lastEmployeeIngestionOn;
			} catch (err) {
				errorLogger("Error fetching last update date:", err as Error);
				throw err;
			}
		},
		enabled: !!location?.locationId,
		staleTime: Infinity,
	});

	useQuery({
		queryKey: ["positionTable", selected?.parent?.branch?._id, selected?.parent?.status?.title, selected?.title],
		queryFn: () => getDataTable(selected),
		enabled: Boolean(selected?.parent?.branch?._id) && Boolean(selected?.title) && Boolean(selected?.parent?.status?.title),
		staleTime: Infinity,
	});

	const handleSelection = (element: object | ISelectedItem) => {
		if (element as ISelectedItem) {
			const branch = findInParentInTree(treeData, { id: element["_id" as keyof object], index: "_id" });
			const positionStatus = branch?.list.find((entry: ISideTreeChild) =>
				(entry?.list ?? []).find((leaf) => leaf["_id" as keyof object] === element["_id" as keyof object])
			);
			const parent: IParent = { branch, status: positionStatus };
			setSelected({ ...element, parent });
		}
	};

	const handleApiCall = () => {
		setIsLoadingMutations(true);
		importPaychexEmployees(locationId).finally(() => setIsLoadingMutations(false));
	};

	const handleLocationClick = async (selectedLocationId: string) => {
		const isLocation = locations?.some((loc) => loc.locationId === selectedLocationId);
		if (!isLocation) {
			return;
		}
		addSubscription(
			["users", selectedLocationId],
			[
				{
					context: "onUserIngestion",
					query: onUserIngestion,
					variables: { locationId: selectedLocationId },
				},
			]
		);
		const queryKey = ["allEmployees", selectedLocationId];
		try {
			const cachedData = queryClient.getQueryData(queryKey);
			if (!cachedData) {
				const loadingTree: ISideTreeChild[] =
					treeData?.map((branch) => {
						if (branch?.["_id" as keyof object] === selectedLocationId) {
							branch["list"] = loadingList(selectedLocationId);
						}
						return branch;
					}) || [];
				setTreeData(loadingTree);
			}
			const employeeData = await queryClient.fetchQuery({
				queryKey,
				queryFn: () => getUsersAndEmployeesPositions(selectedLocationId),
				staleTime: Infinity,
				gcTime: Infinity,
			});

			if (employeeData) {
				queryClient.setQueryData(queryKey, employeeData);
				buildTree(employeeData, selectedLocationId);
			}
		} catch (error) {
			errorLogger("Error fetching employee data:", error as Error);
		}
	};

	const isOperationDisabled = !canPullUserDataFromPayroll || isLoadingMutations;

	return (
		<SnackbarProvider
			maxSnack={MAX_SNACK}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			autoHideDuration={AUTO_HIDE_DURATION}
			iconVariant={{ 
				error: <ErrorOutlineRoundedIcon fontSize="small" className="mr-2" />,
				success: <DoneIcon fontSize="small" className="mr-2" /> 
			}}
			Components={{
				error: SnackbarsCustomStyle,
				success: SnackbarsCustomStyle
			}}
		>
			<BodyLayer
				fixedContent={
					<AppSideBar
						searchProps={{
							loading: loadingLocations,
							onChangeSearchAction: (value) => {
								if (value && selected) {
									setSelected(null);
								}
							},
						}}
						sideBarProps={{
							height: "520px",
							loading: loadingLocations,
							tree: treeData,
							selected: selected?._id ?? "",
							onSelectAction: handleSelection,
							onExpandedAction: (items = []) => handleLocationClick(items?.[0]),
						}}
						actions={{
							alignment: "end",
							loading: loadingLocations,
							operations: [
								{
									identifier: "options",
									classes: "h-[51px] w-[51px] less-opacity-disabled",
									color: "var(--gray-blue-color)",
									icon: (
										<OptionsMenuIcon
											classes={OPTIONS_ICON_CLASSES}
											disabled={isOperationDisabled}
											sx={{ fontSize: "29px" }}
										/>
									),
									iconHover: (
										<OptionsMenuIcon classes={OPTIONS_ICON_CLASSES} hoverVariant={true} sx={{ fontSize: "29px" }} />
									),
									disabled: isOperationDisabled,
									loading: loadingLocations,
									menus: [
										{
											label: "Update",
											disabled: isOperationDisabled,
											classes: "!font-sans text-[16px] !text-default",
											onClick: handleApiCall,
										},
									],
								},
							],
						}}
					/>
				}
			>
				<UsersBody
					position={selected?.title}
					selectionType={selected?.parent?.status?.title}
					selectionLocationId={selected?.parent?.branch?._id}
					isLoadingMutations={isLoadingMutations}
					setIsLoadingMutations={setIsLoadingMutations}
					resetSelects={() => {
						setSelected(null);
					}}
				/>
			</BodyLayer>
		</SnackbarProvider>
	);
};

export default UsersLayer;
